/**
 * Files the user chose, with the folder each came from: the top-level folder
 * they picked or dropped names the unit that recorded it (e.g. "Van7").
 *
 * @module
 */

export interface Picked {
  file: File;
  folder?: string;
}

function topFolder(relativePath: string): string | undefined {
  const parts = relativePath.split("/").filter(Boolean);
  return parts.length > 1 ? parts[0] : undefined;
}

/** From an <input type="file"> (with or without `webkitdirectory`). */
export function fromInput(files: FileList | null): Picked[] {
  return Array.from(files ?? []).map((file) => ({ file, folder: topFolder(file.webkitRelativePath) }));
}

/** From a drop, reading into any dropped folders; `onFound` counts the files so far. */
export async function fromDrop(data: DataTransfer, onFound?: (n: number) => void): Promise<Picked[]> {
  const entries = Array.from(data.items)
    .map((i) => i.webkitGetAsEntry())
    .filter((e): e is FileSystemEntry => e !== null);
  if (!entries.length) return fromInput(data.files);
  const out: Picked[] = [];
  for (const entry of entries) await walk(entry, out, onFound);
  return out;
}

async function walk(entry: FileSystemEntry, out: Picked[], onFound?: (n: number) => void): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    out.push({ file, folder: topFolder(entry.fullPath) });
    onFound?.(out.length);
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries returns at most ~100 at a time; call until it returns none.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    for (const child of batch) await walk(child, out, onFound);
  }
}
